import React from "react";
import { Tabs } from "antd";
import { useTabsGlobal } from "../../routes/views/components/TabsGlobalProvider";
import { useNavigate, useLocation, useParams } from "react-router";
import { UserContext } from "../../routes/views/components/privateroute";
import {
	CloseOutlined,
	LeftOutlined,
	PlusOutlined,
	PushpinOutlined,
	RightOutlined,
} from "@ant-design/icons";
import saveToLocalStorage from "../../functions/saveToLocalStorage";
import axios from "axios";
import CanWrapper from "../Can";

const defaultKeyPrefix = "sprawy";

const makeStorageKeys = (prefix = defaultKeyPrefix) => {
	const safePrefix =
		typeof prefix === "string" && prefix.length > 0
			? prefix
			: defaultKeyPrefix;
	return {
		tableState: `${safePrefix}TableState`,
		navIds: `${safePrefix}NavIds`,
		navMeta: `${safePrefix}NavMeta`,
		currentPage: `${safePrefix}CurrentPage`,
	};
};

const FastEnters = () => {
	const { globalTab, setGlobalTab } = useTabsGlobal();
	const { ticketid } = useParams();
	const globalUser = React.useContext(UserContext);
	const navigate = useNavigate();
	const location = useLocation();
	const params = new URLSearchParams(location.search || "");
	const similar = params.get("similar") === "1";
	const [navLoading, setNavLoading] = React.useState(false);

	const isInactiveRoute =
		typeof location?.pathname === "string" &&
		location.pathname.startsWith("/sprawy/brak-aktywnosci");
	const storageKeyPrefix = isInactiveRoute
		? "brakAktywnosci"
		: similar
			? "sprawySimilar"
			: "sprawy";
	const storageKeys = makeStorageKeys(storageKeyPrefix);
	const ticketRouteBase = isInactiveRoute
		? "/sprawy/brak-aktywnosci/"
		: "/sprawy/";
	const listRouteBase = isInactiveRoute
		? "/sprawy/brak-aktywnosci"
		: "/sprawy/";

	const getNavIds = () => {
		try {
			const raw = localStorage.getItem(storageKeys.navIds);
			const ids = raw ? JSON.parse(raw) : [];
			return Array.isArray(ids)
				? ids.map((x) => Number(x)).filter((x) => Number.isFinite(x))
				: [];
		} catch {
			return [];
		}
	};

	const readTableState = () => {
		try {
			const raw = localStorage.getItem(storageKeys.tableState);
			const parsed = raw ? JSON.parse(raw) : null;
			return parsed && typeof parsed === "object" ? parsed : {};
		} catch {
			return {};
		}
	};

	const readNavMeta = () => {
		try {
			const raw = localStorage.getItem(storageKeys.navMeta);
			const parsed = raw ? JSON.parse(raw) : null;
			return parsed && typeof parsed === "object" ? parsed : {};
		} catch {
			return {};
		}
	};

	const navMeta = readNavMeta();
	const metaActivityMode =
		navMeta?.activityMode === "all" ||
		navMeta?.activityMode === "inactive" ||
		navMeta?.activityMode === "active"
			? navMeta.activityMode
			: null;
	const activityMode =
		metaActivityMode || (isInactiveRoute ? "inactive" : "active");

	const writeTableCurrent = (current) => {
		try {
			const prev = readTableState();
			localStorage.setItem(
				storageKeys.tableState,
				JSON.stringify({ ...prev, current }),
			);
		} catch {}
	};

	const writeNavIds = (ids, metaPatch = {}) => {
		try {
			localStorage.setItem(storageKeys.navIds, JSON.stringify(ids));
		} catch {}
		try {
			const prev = readNavMeta();
			localStorage.setItem(
				storageKeys.navMeta,
				JSON.stringify({ ...prev, ...metaPatch }),
			);
		} catch {}
	};

	const fetchNavPage = async (targetPage) => {
		const tableState = readTableState();
		const pageSize = Number(tableState?.pageSize) || 20;
		const total = Number(tableState?.total);
		if (Number.isFinite(total) && total >= 0) {
			const lastPage = Math.max(1, Math.ceil(total / pageSize));
			if (targetPage < 1 || targetPage > lastPage) return [];
		}

		setNavLoading(true);
		try {
			const resperms = await axios.post(
				process.env.REACT_APP_API_URL + "/perms/get/by/user",
			);
			const userPerms = resperms.data || [];
			const response = await axios.post("/sprawy/list/posts", {
				activityMode,
				pagination: {
					...tableState,
					current: targetPage,
					pageSize,
					search: tableState?.search ?? "",
					orderby: tableState?.orderby ?? [],
				},
				departs: tableState?.departs ?? [],
				isadmin: Array.isArray(userPerms)
					? userPerms.includes("can_see_all_tickets")
					: false,
			});
			const rows = response?.data?.rows || [];
			const ids = rows
				.map((r) => Number(r?.ticketid))
				.filter((id) => Number.isFinite(id));
			writeNavIds(ids, {
				current: targetPage,
				pageSize,
				total: response?.data?.total,
			});
			writeTableCurrent(targetPage);
			return ids;
		} catch {
			return [];
		} finally {
			setNavLoading(false);
		}
	};

	const navIds = getNavIds();
	const currentTicketId = Number(ticketid);
	const currentIndex = Number.isFinite(currentTicketId)
		? navIds.indexOf(currentTicketId)
		: -1;
	const isTicketDetails =
		Number.isFinite(currentTicketId) && currentTicketId > 0;
	const prevTicketId = currentIndex > 0 ? navIds[currentIndex - 1] : null;
	const nextTicketId =
		currentIndex >= 0 && currentIndex < navIds.length - 1
			? navIds[currentIndex + 1]
			: null;
	const canGoPrev = prevTicketId != null;
	const canGoNext = nextTicketId != null;
	const tableState = readTableState();
	const currentPage =
		Number(navMeta?.current) ||
		Number(tableState?.current) ||
		Number(localStorage.getItem(storageKeys.currentPage)) ||
		Number(localStorage.getItem("currentPage")) ||
		1;

	const handleTabClose = async (id, event, favorite) => {
		event.stopPropagation(); // Prevent event from propagating to parent elements
		if (id == ticketid) {
			navigate(listRouteBase);
		}
		if (favorite === true) {
			const response = await axios.post("/sprawy/change/favorite", {
				userid: globalUser.user.mlg_users_id,
				checked: false,
				ticketid: id,
			});
		}
		saveToLocalStorage(
			"tabs",
			globalTab.filter((item) => item.id !== id),
		);
		setGlobalTab(globalTab.filter((item) => item.id !== id));
	};

	const handleTabClick = (id) => {
		navigate(ticketRouteBase + id);
	};

	const goPrev = async () => {
		if (navLoading) return;
		if (canGoPrev) {
			navigate(ticketRouteBase + prevTicketId);
			return;
		}
		// At first item of current page: fetch previous page and jump to its last item.
		if (isTicketDetails && currentIndex === 0 && currentPage > 1) {
			const ids = await fetchNavPage(currentPage - 1);
			const target = ids.length > 0 ? ids[ids.length - 1] : null;
			if (target != null) navigate(ticketRouteBase + target);
		}
	};

	const goNext = async () => {
		if (navLoading) return;
		if (canGoNext) {
			navigate(ticketRouteBase + nextTicketId);
			return;
		}
		// At last item of current page: fetch next page and jump to its first item.
		if (
			isTicketDetails &&
			currentIndex >= 0 &&
			currentIndex === navIds.length - 1
		) {
			const ids = await fetchNavPage(currentPage + 1);
			const target = ids.length > 0 ? ids[0] : null;
			if (target != null) navigate(ticketRouteBase + target);
		}
	};
	return (
		<Tabs
			className="fastEnters"
			activeKey={ticketid ? ticketid : "0"}
		>
			{globalTab.map((tab) => (
				<Tabs.TabPane
					tab={
						<div
							style={{
								display: "flex",
								gap: "10px",
								height: "100%",
								padding: "10px 0",
							}}
							onClick={() => handleTabClick(tab.id)}
						>
							{tab.favorite ? <PushpinOutlined /> : <></>}
							<span className="elipses">{tab.title}</span>
							<CloseOutlined
								onClick={(event) =>
									handleTabClose(tab.id, event, tab.favorite)
								}
							/>
						</div>
					}
					key={tab.id}
				/>
			))}
			<Tabs.TabPane
				tab={
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "8px",
							height: "100%",
							padding: "10px 0",
						}}
					>
						{!isInactiveRoute ? (
							<CanWrapper perms={["can_add_new_tickets"]}>
								<div
									onClick={(e) => {
										e.stopPropagation();
										handleTabClick("nowa");
									}}
									style={{ padding: "0 2px" }}
								>
									<PlusOutlined />
								</div>
							</CanWrapper>
						) : null}
						{isTicketDetails ? (
							<>
								<div
									style={{
										width: 1,
										height: 16,
										background: "rgba(0,0,0,0.15)",
									}}
								/>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "6px",
									}}
								>
									<div
										onClick={(e) => {
											e.stopPropagation();
											goPrev();
										}}
										style={{
											padding: "0 2px",
											opacity:
												navLoading ||
												canGoPrev ||
												(currentIndex === 0 &&
													currentPage > 1)
													? 1
													: 0.3,
											pointerEvents: navLoading
												? "none"
												: "auto",
										}}
									>
										<LeftOutlined />
									</div>
									<div
										onClick={(e) => {
											e.stopPropagation();
											goNext();
										}}
										style={{
											padding: "0 2px",
											opacity:
												navLoading ||
												canGoNext ||
												currentIndex ===
													navIds.length - 1
													? 1
													: 0.3,
											pointerEvents: navLoading
												? "none"
												: "auto",
										}}
									>
										<RightOutlined />
									</div>
								</div>
							</>
						) : null}
					</div>
				}
				key={"nowa"}
			></Tabs.TabPane>
		</Tabs>
	);
};

export default FastEnters;
